import gql from 'graphql-tag';
import graphqlClient from '@shared/graphql/client';

export class TeamService {
  static async update(id, data, manage) {
    const response = await graphqlClient.mutate({
      mutation: gql`
        mutation TEAM_UPDATE(
          $id: String!
          $data: TeamInput!
          $manage: ManageInput!
        ) {
          teamUpdate(id: $id, data: $data, manage: $manage) {
            id
          }
        }
      `,

      variables: {
        id,
        data,
        manage,
      },
    });

    return response.data.teamUpdate;
  }

  static async destroyAll(ids, manage) {
    const response = await graphqlClient.mutate({
      mutation: gql`
        mutation TEAM_DESTROY($ids: [String!]! $manage: ManageInput!) {
          teamDestroy(ids: $ids, manage: $manage)
        }
      `,

      variables: {
        ids,
        manage,
      },
    });

    return response.data.teamDestroy;
  }

  static async create(id, data, manage) {
    const response = await graphqlClient.mutate({
      mutation: gql`
        mutation TEAM_CREATE($id: String! $data: TeamInput! $manage: ManageInput!) {
          teamCreate(id: $id, data: $data, manage: $manage) {
            id
          }
        }
      `,

      variables: {
        id,
        data,
        manage,
      },
    });

    return response.data.teamCreate;
  }

  static async assignSchoolToTeam(id, data, manage) {    
    const response = await graphqlClient.mutate({
      mutation: gql`
        mutation TEAM_ASSIGN_SCHOOL($id: String! $data: SchoolInput! $manage: ManageInput!) {
          teamAssignSchoolToTeam(id: $id, data: $data, manage: $manage)
        }
      `,

      variables: {
        id,
        data,
        manage,
      },
    });

    return response.data.teamCreate;
  }

  static async import(values, importHash, manage) {
    const response = await graphqlClient.mutate({
      mutation: gql`
        mutation TEAM_IMPORT(
          $data: TeamInput!
          $importHash: String!
          $manage: ManageInput!
        ) {
          teamImport(data: $data, importHash: $importHash, manage: $manage)
        }
      `,

      variables: {
        data: values,
        importHash,
        manage,
      },
    });

    return response.data.teamImport;
  }

  static async findForNba(id, manage) {
    const response = await graphqlClient.query({
      query: gql`
        query TEAM_FIND($id: String! $manage: ManageInput!) {
          teamFind(id: $id, manage: $manage) {
            id
            name
            groupsPhase
            colorCodes
            school {
              name
            }
            logo {
              id
              name
              sizeInBytes
              publicUrl
              privateUrl
            }
            programs
            createdAt
            updatedAt
          }
        }
      `,

      variables: {
        id,
        manage,
      },
    });

    return response.data.teamFind;
  }

  static async find(id, manage) {
    const response = await graphqlClient.query({
      query: gql`
        query TEAM_FIND($id: String! $manage: ManageInput!) {
          teamFind(id: $id, manage: $manage) {
            id
            name
            groupsPhase
            colorCodes
            school {
              id
              name
              location {
                name
              }
            }
            logo {
              id
              name
              sizeInBytes
              publicUrl
              thumbPublicUrl
              privateUrl
            }
            players {
              id
              firstName
              lastName
              gender
              birthdate {
                day
                month
                year
              }
              picture {
                id
                name
                type
                sizeInBytes
                publicUrl
                privateUrl
              }
              teams {
                id
                name
              }
            }
            coaches {
              id
              firstName
              lastName
              type
              email
              teams {
                id
                name
              }
            }
            programs
            program {
              id
              name
            }
            subprogram {
              id
              name
            }
            createdAt
            updatedAt
          }
        }
      `,

      variables: {
        id,
        manage,
      },
    });

    return response.data.teamFind;
  }

  static async newId(manage) {
    const response = await graphqlClient.query({
      query: gql`
        query TEAM_NEW_ID ($manage: ManageInput!){
          teamNewId(manage: $manage)
        }
      `,
      variables: {
        manage,
      },
    });
    return response.data.teamNewId;
  }

  static async unmatch(teamId, schoolId, manage) {    
    const response = await graphqlClient.query({
      query: gql`
        query TEAM_UNMATCH(
          $teamId: String!
          $schoolId: String!
          $manage: ManageInput!
        ) {
          teamUnmatch(
            teamId: $teamId
            schoolId: $schoolId
            manage: $manage
          )
        }
      `,
      variables: {
        teamId,
        schoolId,
        manage
      },
    });
    return response.data.teamUnmatch;
  }

  static async list(filter, orderBy, limit, offset, manage) {
    if ( !manage.subpropertyId ) return { rows: [], count: 0};
    
    const response = await graphqlClient.query({
      query: gql`
        query TEAM_LIST(
          $filter: TeamFilterInput
          $orderBy: TeamOrderByEnum
          $limit: Int
          $offset: Int
          $manage: ManageInput!
        ) {
          teamList(
            filter: $filter
            orderBy: $orderBy
            limit: $limit
            offset: $offset
            manage: $manage
          ) {
            count
            rows {
              id
              name
              groupsPhase
              logo {
                id
                name
                sizeInBytes
                publicUrl
                privateUrl
              }
              players{
                id
                firstName
                lastName
                gender
                birthdate {
                  day
                  month
                  year
                }
                picture {
                  id
                  name
                  type
                  sizeInBytes
                  publicUrl
                  privateUrl
                }
                teams {
                  id
                  name
                }
              }
              coaches {
                id
                firstName
                lastName
                type
                email
                teams {
                  id
                  name
                }
              }
              programs
              program { 
                id
                name 
              }
              subprogram { 
                id
                name 
              }
              updatedAt
              createdAt
            }
          }
        }
      `,

      variables: {
        filter,
        orderBy,
        limit,
        offset,
        manage,
      },
    });
    
    return response.data.teamList;
  }

  static async listForNba(filter, orderBy, limit, offset, manage) {  
    const response = await graphqlClient.query({
      query: gql`
        query TEAM_LIST(
          $filter: TeamFilterInput
          $orderBy: TeamOrderByEnum
          $limit: Int
          $offset: Int
          $manage: ManageInput!
        ) {
          teamList(
            filter: $filter
            orderBy: $orderBy
            limit: $limit
            offset: $offset
            manage: $manage
          ) {
            count
            rows {
              id
              name
              program {
                id
                name
              }
              subprogram {
                id
                name
              }
              groupsPhase
              logo {
                id
                name
                sizeInBytes
                publicUrl
                thumbPublicUrl
                privateUrl
              }
              school {
                id
                name
              }
              programs
              updatedAt
              createdAt
            }
          }
        }
      `,

      variables: {
        filter,
        orderBy,
        limit,
        offset,
        manage,
      },
    });

    return response.data.teamList;
  }
  
  static async listTransfer(filter, orderBy, limit, offset, manage) {
    const response = await graphqlClient.query({
      query: gql`
        query TEAM_LIST(
          $filter: TeamFilterInput
          $orderBy: TeamOrderByEnum
          $limit: Int
          $offset: Int
          $manage: ManageInput!
        ) {
          teamList(
            filter: $filter
            orderBy: $orderBy
            limit: $limit
            offset: $offset
            manage: $manage
          ) {
            count
            rows {
              id
              name
              program {
                id
                name
              }
              subprogram {
                id
                name
              }
            }
          }
        }
      `,

      variables: {
        filter,
        orderBy,
        limit,
        offset,
        manage,
      },
    });

    return response.data.teamList;
  }

  static async listAutocomplete(query, limit, manage) {
    if ( !manage.subpropertyId ) return { rows: [], count: 0};
    const response = await graphqlClient.query({
      query: gql`
        query TEAM_AUTOCOMPLETE(
          $query: String
          $limit: Int
          $manage: ManageInput!
        ) {
          teamAutocomplete(query: $query, limit: $limit, manage: $manage) {
            id
            label
          }
        }
      `,

      variables: {
        query,
        limit,
        manage,
      },
    });

    return response.data.teamAutocomplete;
  }
}
